import { useCallback, useRef } from 'react';
import { useTaskStore } from '@/stores/taskStore';
import { useNodeStore } from '@/stores/nodeStore';
import { useAgentStore } from '@/stores/agentStore';
import { NodeType, NodeStatus } from '@/types';
import { eventBus, CanvasEvents } from '@/utils/eventBus';
import { saveAssets } from '@/utils/assetUtils';
import { createLocalId } from '../helpers';

export function useTaskUpdates() {
  const processedTaskUpdates = useRef<Set<string>>(new Set());

  const updateMessageWithTaskResult = useCallback(
    (taskId: string, status: string, result?: any, error?: string) => {
      useAgentStore.getState().setMessages((prev) =>
        prev.map((m) => {
          const matchByMediaData = m.mediaData && m.mediaData.taskId === taskId;
          const matchByMetadata = m.metadata?.pendingTaskId === taskId;

          if (matchByMediaData || matchByMetadata) {
            const newStatus = status === 'success' || status === 'SUCCESS' ? 'SUCCESS' : 'FAILED';

            return {
              ...m,
              mediaData: m.mediaData
                ? {
                    ...m.mediaData,
                    status: newStatus,
                    results: result?.results || m.mediaData?.results || [],
                    ...(result?.videoMetadata ? { videoMetadata: result.videoMetadata } : {}),
                    ...(error ? { error } : {}),
                  }
                : undefined,
              text: '',
              metadata: {
                ...m.metadata,
                taskStatus: status === 'success' || status === 'SUCCESS' ? 'COMPLETED' : 'FAILED',
              },
            };
          }
          return m;
        })
      );
    },
    []
  );

  const updateMessageWithTaskRunning = useCallback((taskId: string) => {
    useAgentStore.getState().setMessages((prev) =>
      prev.map((m) => {
        const matchByMediaData = m.mediaData && m.mediaData.taskId === taskId;
        const matchByMetadata = m.metadata?.pendingTaskId === taskId;

        if (matchByMediaData || matchByMetadata) {
          return {
            ...m,
            ...(m.mediaData ? { mediaData: { ...m.mediaData, status: 'RUNNING' } } : {}),
            metadata: { ...m.metadata, taskStatus: 'RUNNING' },
          };
        }
        return m;
      })
    );
  }, []);

  const updateCanvasNode = useCallback(
    async (taskId: string, nodeId: string, status: string, updates?: any) => {
      const store = useAgentStore.getState();
      if (store.pendingCanvasNodeId === nodeId) {
        store.setPendingCanvasNodeId(null);
      }

      // 更新画布节点
      const node = useNodeStore.getState().nodeMap.get(nodeId);
      if (node && node.status === NodeStatus.WORKING) {
        const result = updates || {};
        const isVideo = node.type === NodeType.VIDEO_GENERATOR;
        const results: Array<{ url: string; outputType?: string }> = result.results || [];
        const urls = results.map((r: any) => r.url).filter(Boolean) as string[];

        if (urls.length > 0) {
          const projectId = store.projectId;
          let savedUrls = urls;

          if (projectId) {
            try {
              const { savedUrls: su } = await saveAssets(projectId, urls, {
                nodeId,
                prefix: `agent_${isVideo ? 'video' : 'image'}`,
                metadata: {
                  generator_node_id: nodeId,
                  generator_type: isVideo ? 'agent_video' : 'agent_image',
                },
                isVideo,
              });
              savedUrls = su;
            } catch (err) {
              console.error('[useTaskUpdates] 保存资产失败:', err);
            }
          }

          const nodeUpdates: Record<string, any> = { progress: undefined };
          if (isVideo) {
            nodeUpdates.videoUri = savedUrls[0] || urls[0];
            nodeUpdates.videoUris = savedUrls.length > 0 ? savedUrls : urls;
            if (result.videoMetadata) {
              nodeUpdates.videoMetadata = result.videoMetadata;
              nodeUpdates.duration = typeof result.videoMetadata.duration === 'number'
                ? result.videoMetadata.duration
                : undefined;
            }
          } else {
            nodeUpdates.image = savedUrls[0] || urls[0];
            nodeUpdates.images = savedUrls.length > 0 ? savedUrls : urls;
          }

          useNodeStore.getState().updateNode(
            nodeId,
            nodeUpdates,
            undefined,
            undefined,
            NodeStatus.SUCCESS
          );
          console.log(`[useTaskUpdates] Updated canvas node ${nodeId} with ${isVideo ? 'video' : 'image'} result`);
        } else {
          // 没有结果 URL，仅更新状态
          useNodeStore.getState().updateNode(
            nodeId,
            { progress: undefined },
            undefined,
            undefined,
            NodeStatus.SUCCESS
          );
        }
      }
    },
    []
  );

  const handleWSMessage = useCallback((msg: any) => {
    const store = useAgentStore.getState();

    // 处理状态消息
    if (msg.type === 'status') {
      if (msg.status === 'thinking') {
        store.setIsThinking(true);
        store.setThinkingSeconds(0);
        store.startThinkingTimer();
      } else if (msg.status === 'done') {
        store.clearThinkingTimer();
        store.setIsThinking(false);
      }
    }

    // 处理结果消息
    if (msg.type === 'result') {
      // ... 简化的结果处理
      store.setIsLoading(false);
    }

    // 处理错误消息
    if (msg.type === 'error') {
      store.setMessages((prev) => [...prev, {
        role: 'model',
        text: msg.message ?? '发生错误'
      }]);
      store.setIsLoading(false);
      store.clearThinkingTimer();
      store.setIsThinking(false);
    }

    // 处理任务更新
    if (msg.type === 'task_update' && msg.task_id) {
      const updatedTaskId = msg.task_id;
      const updatedStatus = msg.data?.status;
      const updatedResult = msg.data?.result;
      const updatedError = msg.data?.error;

      if (updatedStatus === 'success' || updatedStatus === 'failed') {
        if (processedTaskUpdates.current.has(updatedTaskId)) return;
        processedTaskUpdates.current.add(updatedTaskId);
        setTimeout(() => processedTaskUpdates.current.delete(updatedTaskId), 5000);

        updateMessageWithTaskResult(updatedTaskId, updatedStatus, updatedResult, updatedError);
      } else if (updatedStatus === 'running') {
        updateMessageWithTaskRunning(updatedTaskId);
      }
    }
  }, [updateMessageWithTaskResult, updateMessageWithTaskRunning]);

  const handleTaskCompleted = useCallback(
    async (data: { taskId: string; nodeId: string; status: string; updates?: any }) => {
      if (processedTaskUpdates.current.has(data.taskId)) return;
      processedTaskUpdates.current.add(data.taskId);
      setTimeout(() => processedTaskUpdates.current.delete(data.taskId), 5000);

      updateMessageWithTaskResult(data.taskId, data.status, data.updates);
      await updateCanvasNode(data.taskId, data.nodeId, data.status, data.updates);
    },
    [updateMessageWithTaskResult, updateCanvasNode]
  );

  const handleTaskStatusChanged = useCallback(
    (data: { taskId: string; nodeId: string; status: string }) => {
      updateMessageWithTaskRunning(data.taskId);
    },
    [updateMessageWithTaskRunning]
  );

  return {
    handleWSMessage,
    handleTaskCompleted,
    handleTaskStatusChanged,
  };
}
